/**
 * AppFooter
 *
 * Phase 999.100 W4 (D-16 + W4-D-07). Lightweight global footer with the
 * regulatory-position links the launch is required to surface:
 *   - About AfterService AI (/about-ai) - public regulatory-position page (in-app)
 *   - Privacy Policy        (https://afterservice.ai/privacy) - single canonical
 *   - Terms & Conditions    (https://afterservice.ai/terms) - single canonical
 *   - Crisis Support        (/crisis)
 *
 * 2026-05-10 cofounder decision (audit MF-equivalent #5): privacy policy
 * consolidates to a single canonical URL on the marketing site. The in-app
 * /legal/privacy stub is removed in this phase. Best-practice rationale:
 *   - APP 1.5 requires "available free of charge" - public marketing-site URL
 *     is the cleanest meet (no auth required).
 *   - App-store listings demand a public privacy URL; canonical reduces drift.
 *   - Single source of truth - one place to update, one place to audit.
 *
 * Renders inside AppLayout once W2/W4 wire it in. Kept presentational and
 * stateless so it can be dropped into authenticated and unauthenticated
 * surfaces alike.
 */

import React from 'react';
import { Box, Typography, Link as MuiLink, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const T = {
  textBody: '#475569',
  textMuted: '#64748b',
  border: '#e2e8f0',
  surfaceMuted: '#f8fafc',
  brand: 'var(--as-blue-500)',
};

const FOOTER_LINKS = [
  { label: 'About AfterService AI', to: '/about-ai' },
  { label: 'Privacy Policy', href: 'https://afterservice.ai/privacy', external: true },
  { label: 'Terms & Conditions', href: 'https://afterservice.ai/terms', external: true },
  { label: 'Crisis Support', to: '/crisis' },
];

export default function AppFooter() {
  const navigate = useNavigate();

  const handleInternal = (to) => (event) => {
    event.preventDefault();
    navigate(to);
  };

  return (
    <Box
      component="footer"
      role="contentinfo"
      sx={{
        width: '100%',
        bgcolor: T.surfaceMuted,
        borderTop: `1px solid ${T.border}`,
        px: { xs: 2, sm: 4 },
        py: 2.5,
        mt: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box
          component="nav"
          aria-label="Footer"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1.25, sm: 2 },
            alignItems: 'center',
          }}
        >
          {FOOTER_LINKS.map((link, idx) => (
            <React.Fragment key={link.label}>
              {idx > 0 && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: 'none', sm: 'block' }, borderColor: T.border, height: 14, alignSelf: 'center' }}
                  aria-hidden="true"
                />
              )}
              {link.external ? (
                <MuiLink
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={footerLinkStyle}
                >
                  {link.label}
                </MuiLink>
              ) : (
                <MuiLink
                  href={link.to}
                  onClick={handleInternal(link.to)}
                  sx={footerLinkStyle}
                >
                  {link.label}
                </MuiLink>
              )}
            </React.Fragment>
          ))}
        </Box>

        <Typography sx={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
          &copy; {new Date().getFullYear()} AfterService.ai &middot; Built in Australia.
        </Typography>
      </Box>
    </Box>
  );
}

const footerLinkStyle = {
  fontSize: 13,
  fontWeight: 500,
  color: T.textBody,
  textDecoration: 'none',
  '&:hover': { color: T.brand, textDecoration: 'underline' },
  '&:focus-visible': {
    outline: `2px solid ${T.brand}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
};
